// DOM-touching share helpers. INTERNAL — components do not import these
// directly. Use `useCopyShareLink` or `<ShareModal>` from the lib's public
// surface instead.

import { logger } from '@/lib/telemetry/logger';

import { emitShareEvent, type ShareSurface } from './events';
import { buildShareIntent, type ShareChannel, type ShareContent } from './intents';

export type NativeShareResult = 'shared' | 'aborted' | 'unsupported' | 'error';

export function canNativeShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export interface NativeShareInput {
  url: string;
  text?: string;
  title?: string;
  /** Surface for analytics. Required — every share emits an event. */
  surface: ShareSurface;
}

export async function nativeShare(input: NativeShareInput): Promise<NativeShareResult> {
  if (!canNativeShare()) return 'unsupported';
  try {
    const payload: ShareData = { url: input.url };
    if (input.text !== undefined) payload.text = input.text;
    if (input.title !== undefined) payload.title = input.title;
    await navigator.share(payload);
    emitShareEvent(input.surface, 'native');
    return 'shared';
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') return 'aborted';
    logger.warn('share.nativeShare failed', {
      message: err instanceof Error ? err.message.slice(0, 200) : 'unknown',
    });
    return 'error';
  }
}

export async function copyToClipboard(value: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || typeof navigator.clipboard === 'undefined') {
    return execCommandFallback(value);
  }
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (err: unknown) {
    logger.warn('share.copyToClipboard primary path failed; falling back to execCommand', {
      message: err instanceof Error ? err.message.slice(0, 200) : 'unknown',
    });
    return execCommandFallback(value);
  }
}

function execCommandFallback(value: string): boolean {
  if (typeof document === 'undefined') return false;
  const input = document.createElement('input');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'absolute';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.select();
  try {
    return document.execCommand('copy');
  } catch (err: unknown) {
    logger.warn('share.execCommandFallback failed', {
      message: err instanceof Error ? err.message.slice(0, 200) : 'unknown',
    });
    return false;
  } finally {
    document.body.removeChild(input);
  }
}

export interface OpenShareIntentInput extends ShareContent {
  surface: ShareSurface;
}

export function openShareIntent(channel: ShareChannel, input: OpenShareIntentInput): void {
  if (typeof window === 'undefined') return;
  const content: ShareContent = {
    url: input.url,
    ...(input.text !== undefined ? { text: input.text } : {}),
    ...(input.subject !== undefined ? { subject: input.subject } : {}),
  };
  const url = buildShareIntent(channel, content);
  window.open(url, '_blank', 'noopener,noreferrer');
  emitShareEvent(input.surface, channel);
}
